import { Box, Flex, Grid, Td, Text, Skeleton, useToast, Alert, AlertIcon, AlertTitle, CloseButton } from '@chakra-ui/react'
import { HeadingPages } from '../../../components/HeadingPages'
import { Input } from '../../../components/Form'
import { SelectComponent } from '../../../components/Form/Select'
import * as T from '../../../components/Table'
import { useForm } from 'react-hook-form'
import { Button } from '../../../components/Button'
import { api } from '../../../services/api'
import { useQuery } from 'react-query'
import { Pagination } from '../../../components/Pagination'
import React, { useEffect, useCallback } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { CidadeItem } from './CidadeItem'
import { CidadeItemSkeleton } from './CidadeItemSkeleton'

const searchSchema = z.object({
  title: z.string().optional(),
  keyword: z.string().optional(),
  category: z
    .object({
      label: z.string().optional(),
      value: z.string().optional(),
    })
    .optional(),
  initialDate: z.string().optional(),
  finalDate: z.string().optional(),
  publicado: z
    .object({
      label: z.string().optional(),
      value: z.boolean().optional(),
    })
    .optional(),
})

async function getAllCidades({ page, limit, aprovados, ...search }) {
  const response = await api.get(`/cidades-home/listar-cidades-cadastradas`, {
    params: {
      page,
      limit,
      q: search.title,
      data_inicial: search.initialDate,
      data_final: search.finalDate,
      categoria: search.category?.value,
      keyword: search.keyword,
      aprovados: search.publicado?.value || false,
    },
  })
  return response.data
}

export function Cidades() {
  const { control, handleSubmit, register, formState, setValue, reset } = useForm({
    resolver: zodResolver(searchSchema),
  })

  const [currentPage, setCurrentPage] = React.useState(1)
  const [isErrorVisible, setIsErrorVisible] = React.useState(false)
  const toast = useToast()

  const [search, setSearch] = React.useState({
    title: '',
    keyword: '',
    category: null,
    initialDate: '',
    finalDate: '',
    publicado: { label: 'Não', value: false },
  })

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['cidades', { page: currentPage, limit: 30, ...search }],
    queryFn: () => getAllCidades({ page: currentPage, limit: 20, ...search }),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5, // 5 minutos
    retry: 2,
    onError: () => {
      setIsErrorVisible(true)
    },
  })

  const handleSearch = useCallback(
    async (formData) => {
      setSearch(formData)
      setCurrentPage(1) // Resetar para a primeira página ao buscar

      toast({
        title: 'Buscando cidades...',
        status: 'info',
        duration: 2000,
        isClosable: true,
        position: 'top-right',
      })
    },
    [toast]
  )

  const handleResetSearch = useCallback(() => {
    reset({
      title: '',
      keyword: '',
      category: { label: 'Selecione...', value: '' },
      initialDate: '',
      finalDate: '',
      publicado: { label: 'Não', value: false },
    })

    setSearch({
      title: '',
      keyword: '',
      category: { label: 'Selecione...', value: '' },
      initialDate: '',
      finalDate: '',
      publicado: { label: 'Não', value: false },
    })

    setCurrentPage(1)
  }, [reset])

  useEffect(() => {
    setValue('publicado', { label: 'Não', value: false })
    setValue('category', { label: 'Selecione...', value: '' })
  }, [setValue])

  return (
    <>
      <HeadingPages name="Cidades" />

      <Flex flexDir="column" gap="20px" mt="20px" maxW="1280px" px="12px" mx="auto" mb="10">
        {isErrorVisible && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            <AlertTitle mr={2}>Erro ao carregar os dados. Tente novamente.</AlertTitle>
            <CloseButton position="absolute" right="8px" top="8px" onClick={() => setIsErrorVisible(false)} />
          </Alert>
        )}

        <Grid as="form" gridTemplateColumns={'666px 1fr'} gap={'12px'} onSubmit={handleSubmit(handleSearch)}>
          <Input label="Pesquisar por título" {...register('title')} />
          <Flex gap="12px">
            <SelectComponent
              name="publicado"
              label="Publicado"
              control={control}
              options={[
                { label: 'Sim', value: true },
                { label: 'Não', value: false },
              ]}
            />
          </Flex>

          <Flex gap="5px" alignItems="flex-end" gridColumnStart={4}>
            <Input label="Data Inicial" type="date" {...register('initialDate')} />
            <Input label="Data Final" type="date" {...register('finalDate')} />
            <Button size="sm" type="submit" px="30px" isLoading={formState.isSubmitting || isFetching} isDisabled={isFetching}>
              {isFetching ? 'Buscando...' : 'Buscar'}
            </Button>
            <Button size="sm" variant="outline" colorScheme="gray" px="15px" onClick={handleResetSearch} isDisabled={isFetching || isLoading}>
              Limpar
            </Button>
          </Flex>
        </Grid>

        {isLoading || isFetching ? (
          <Skeleton height="40px" width="100%" borderRadius="md" />
        ) : (
          data && (
            <Flex ml="auto" gap={2} alignItems="center" p={1} rounded="md" bg="gray.200" border="1px solid" borderColor="gray.300" boxShadow="sm">
              <Text color="red.500" whiteSpace="nowrap" fontSize="sm" fontWeight="light">
                <strong>{data.total_em_aprovacao || 0}</strong> cidade(s) não aprovadas
              </Text>
              <Box w="1px" h="20px" bgColor="gray.300" />
              <Text color="green.500" whiteSpace="nowrap" fontSize="sm" fontWeight="light">
                <strong>{data?.total_aprovados || 0}</strong> cidade(s) aprovadas
              </Text>
              <Box w="1px" h="20px" bgColor="gray.300" />
              <Text color="blue.500" whiteSpace="nowrap" fontSize="sm" fontWeight="light">
                <strong>{data.total_aprovado_e_em_aprovacao || 0}</strong> total
              </Text>
            </Flex>
          )
        )}

        <T.Table>
          <T.Thead>
            <T.Tr>
              <T.Th textAlign="start" px={2}>
                Cidade
              </T.Th>
              <T.Th>Data de publicação</T.Th>
              <T.Th>Data aprovação</T.Th>
              <T.Th />
              <T.Th />
            </T.Tr>
          </T.Thead>
          <T.Tbody>
            {isLoading ||
              (isFetching && (
                <>
                  {[...Array(5)].map((_, index) => (
                    <CidadeItemSkeleton key={index} />
                  ))}
                </>
              ))}

            {error && !isLoading && (
              <Td colSpan={5} textAlign="center" py={4}>
                <Flex justifyContent="center" alignItems="center" direction="column" gap={2}>
                  <Text fontSize="lg" fontWeight="bold" color="red.500">
                    Erro ao carregar os dados
                  </Text>
                  <Button size="sm" colorScheme="blue" onClick={() => refetch()}>
                    Tentar novamente
                  </Button>
                </Flex>
              </Td>
            )}

            {!isLoading && !isFetching && !error && data?.cidades.length === 0 && (
              <Td colSpan={5} textAlign="center" py={4}>
                <Text fontSize="lg" fontWeight="bold" color="gray.400">
                  Nenhuma cidade encontrada
                </Text>
              </Td>
            )}

            {!isLoading && !error && data?.cidades.map((cidade) => <CidadeItem key={cidade.id} cidade={cidade} currentPage={currentPage} />)}
          </T.Tbody>
        </T.Table>

        {!isLoading && !error && data?.cidades.length > 0 && (
          <Pagination page={currentPage} setPage={setCurrentPage} limit={30} totalPages={data?.last_page} isDisabled={isFetching} />
        )}
      </Flex>
    </>
  )
}
